import { zodResolver } from '@hookform/resolvers/zod'
import { CheckIcon, PlusIcon, QuestionIcon } from '@phosphor-icons/react'
import { useMemo, useRef, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { BookingQuestion, useSaveBookingQuestion } from '@/api/booking-types'
import { SelectFormField } from '@/components/form'
import { TextAreaFormField } from '@/components/form/text-area-form-field'
import { TextInputFormField } from '@/components/form/text-input-form-field'
import { Button } from '@/components/ui/button'
import { Flex } from '@/components/ui/flex'
import { Grid } from '@/components/ui/grid'
import { Icon } from '@/components/ui/icon'
import { Modal } from '@/components/ui/modal'
import { SelectOption } from '@/components/ui/select'
import { toastFactory } from '@/factories'
import { useDirtyFormGuard } from '@/hooks'
import { FormChangeFields, getFormChanges } from '@/pages/organisation/shared/form-changes'
import { PersonFormLayout } from '@/pages/organisation/shared/person-form-layout'
import { SwitchRow } from '@/pages/organisation/shared/switch-row'
import { formatDollars } from '@/utils/currency'
import { getApiErrorMessage } from '@/utils/error'

import { UpdatedAt } from '../../shared/updated-at'
import { QuestionAnswers, QuestionAnswersHandle } from './question-answers'
import {
  ANSWER_TYPE_LABEL_KEYS,
  ANSWER_TYPES,
  CUSTOM_PRICE,
  getQuestionFormDefaults,
  IMAGE_LIMIT_DEFAULT,
  makeQuestionFormSchema,
  PRICING_TYPE_LABEL_KEYS,
  QUESTION_MODAL_MAX_LENGTH,
  QUESTION_TITLE_MAX_LENGTH,
  QuestionFormData,
  SELECT_TYPES,
} from './question-form-modal.constants'
import { buildSaveQuestionRequest } from './question-form-modal.utils'

interface QuestionFormModalProps {
  bookingTypeId: number
  nextOrder: number
  onClose: VoidFunction
  onSaved?: (id: number) => void
  question?: BookingQuestion
}

export const QuestionFormModal = ({
  bookingTypeId,
  nextOrder,
  onClose,
  onSaved,
  question,
}: QuestionFormModalProps) => {
  const { t } = useTranslation(['organisation', 'common'])
  const { mutateAsync: saveQuestion } = useSaveBookingQuestion()
  const isEdit = !!question
  const answersRef = useRef<QuestionAnswersHandle>(null)
  const [isAnswerFormOpen, setIsAnswerFormOpen] = useState(false)

  const schema = useMemo(() => makeQuestionFormSchema(t), [t])

  const form = useForm<QuestionFormData>({
    defaultValues: getQuestionFormDefaults(question),
    mode: 'onTouched',
    resolver: zodResolver(schema),
  })

  const { isDirty, isSubmitting } = form.formState
  const answersType = form.watch('answersType')
  const guard = useDirtyFormGuard(
    isDirty && !isSubmitting,
    () =>
      getFormChanges({
        defaultValues: form.formState.defaultValues,
        fields: changeFields,
        values: form.getValues(),
      }),
    { inPopup: true },
  )

  const handleClose = () => {
    guard(onClose)
    return false
  }

  const answerTypeOptions = useMemo<SelectOption<string>[]>(
    () =>
      ANSWER_TYPES.map((value) => ({
        label: t(
          `organisation:bookingTypes.question.fields.answerType.options.${ANSWER_TYPE_LABEL_KEYS[value]}`,
        ),
        value,
      })),
    [t],
  )

  const selectTypeOptions = useMemo<SelectOption<string>[]>(
    () =>
      SELECT_TYPES.map((value) => ({
        label: t(`organisation:bookingTypes.question.fields.selectType.${value}`),
        value,
      })),
    [t],
  )

  const changeFields = useMemo<FormChangeFields<QuestionFormData>>(
    () => ({
      answers: {
        items: (value) =>
          (value as QuestionFormData['answers']).map((answer) =>
            [
              answer.title || '-',
              t(
                `organisation:bookingTypes.question.answers.pricingType.options.${PRICING_TYPE_LABEL_KEYS[answer.pricingType as keyof typeof PRICING_TYPE_LABEL_KEYS]}`,
              ),
              answer.pricingType === CUSTOM_PRICE
                ? answer.pricingValue
                : formatDollars(answer.pricingValue, { zeroPrice: true }),
            ]
              .filter(Boolean)
              .join(' · '),
          ),
        label: t('organisation:bookingTypes.question.answers.label'),
      },
      answersType: {
        format: (value) =>
          t(
            `organisation:bookingTypes.question.fields.answerType.options.${ANSWER_TYPE_LABEL_KEYS[value as keyof typeof ANSWER_TYPE_LABEL_KEYS]}`,
          ),
        label: t('organisation:bookingTypes.question.fields.answerType.label'),
      },
      imageLimit: { label: t('organisation:bookingTypes.question.fields.imageLimit.label') },
      isRequired: {
        format: (value) => t(value ? 'common:booleans.yes' : 'common:booleans.no'),
        label: t('organisation:bookingTypes.question.fields.required.label'),
      },
      modal: { label: t('organisation:bookingTypes.question.fields.modal.label') },
      selectType: {
        format: (value) =>
          t(
            `organisation:bookingTypes.question.fields.selectType.${value as 'multiple' | 'single'}`,
          ),
        label: t('organisation:bookingTypes.question.fields.selectType.label'),
      },
      title: { label: t('organisation:bookingTypes.question.fields.title.label') },
    }),
    [t],
  )

  const onSubmit: SubmitHandler<QuestionFormData> = async (values) => {
    const request = buildSaveQuestionRequest({
      bookingTypeId,
      defaultAnswer: {
        placeholder: t('organisation:booking.type.answer.defaultPlaceholder'),
        title: t('organisation:booking.type.answer.defaultTitle'),
      },
      existing: question,
      nextOrder,
      values,
    })

    try {
      const saved = await saveQuestion(request)
      toastFactory.success(
        t(
          isEdit
            ? 'organisation:bookingTypes.question.toasts.updated'
            : 'organisation:bookingTypes.question.toasts.created',
        ),
      )
      if (!isEdit) onSaved?.(saved.id)
      onClose()
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  }

  const submit = form.handleSubmit(onSubmit)
  const submitDisabled = isEdit && !isDirty

  return (
    <Modal
      autoWidth
      title={
        isEdit
          ? t('organisation:bookingTypes.question.titleEdit')
          : t('organisation:bookingTypes.question.titleNew')
      }
      headerIcon={QuestionIcon}
      footer={
        <Flex align="center" flex={1} gap={12} justify="space-between">
          <Flex align="center" gap={12}>
            {answersType === 'button' && (
              <Button
                disabled={isAnswerFormOpen}
                use="secondary"
                onClick={() => answersRef.current?.add()}
              >
                {t('organisation:bookingTypes.question.answers.add')}
                <Icon icon={PlusIcon} />
              </Button>
            )}
            <UpdatedAt value={question?.updatedAt} />
          </Flex>
          <Flex gap={12}>
            <Button use="secondary" onClick={handleClose}>
              {t('common:actions.cancel')}
            </Button>
            <Button disabled={submitDisabled} loading={isSubmitting} use="primary" onClick={submit}>
              {isEdit ? t('common:actions.save') : t('common:actions.add')}
              <Icon icon={isEdit ? CheckIcon : PlusIcon} />
            </Button>
          </Flex>
        </Flex>
      }
      onBeforeClose={handleClose}
      onClose={onClose}
    >
      <PersonFormLayout submitDisabled={submitDisabled} form={form} onSubmit={submit}>
        <TextInputFormField
          withReservedFooter
          required
          label={t('organisation:bookingTypes.question.fields.title.label')}
          maxLength={QUESTION_TITLE_MAX_LENGTH}
          name="title"
          placeholder={t('organisation:bookingTypes.question.fields.title.placeholder')}
        />

        <TextAreaFormField
          withReservedFooter
          label={t('organisation:bookingTypes.question.fields.modal.label')}
          maxLength={QUESTION_MODAL_MAX_LENGTH}
          name="modal"
          rows={2}
          placeholder={t('organisation:bookingTypes.question.fields.modal.placeholder')}
        />

        <Grid align="start" columns="repeat(2, minmax(0, 1fr))" gap={8}>
          <SelectFormField<QuestionFormData, string>
            withReservedFooter
            label={t('organisation:bookingTypes.question.fields.answerType.label')}
            name="answersType"
            options={answerTypeOptions}
            size="medium"
            type="single"
          />
          <SelectFormField<QuestionFormData, string>
            withReservedFooter
            label={t('organisation:bookingTypes.question.fields.selectType.label')}
            name="selectType"
            options={selectTypeOptions}
            size="medium"
            type="single"
          />
        </Grid>

        <SwitchRow
          label={t('organisation:bookingTypes.question.fields.required.label')}
          name="isRequired"
        />

        {answersType === 'image' && (
          <TextInputFormField
            withReservedFooter
            required
            helperText={t('organisation:bookingTypes.question.fields.imageLimit.helper', {
              count: IMAGE_LIMIT_DEFAULT,
            })}
            inputMode="numeric"
            label={t('organisation:bookingTypes.question.fields.imageLimit.label')}
            maxLength={2}
            name="imageLimit"
            placeholder={String(IMAGE_LIMIT_DEFAULT)}
          />
        )}

        {answersType === 'button' && (
          <QuestionAnswers ref={answersRef} onFormOpenChange={setIsAnswerFormOpen} />
        )}
      </PersonFormLayout>
    </Modal>
  )
}
